// Optimistic writes: the screen shows the result at once and rolls back on failure.
//
// Promise of `optimisticMutation` (spread into useMutation's options):
// - onMutate cancels fetches in flight (a late answer must not overwrite the
//   optimistic value), keeps the previous value and writes `apply(old, input)`.
//   When `apply` returns undefined the cache is left alone.
// - onError puts the previous value back; onSettled refetches the key either way.
// It handles one cache entry; a mutation that touches several writes its own.
import type { QueryClient, QueryKey } from "@tanstack/react-query";

export const OPTIMISTIC_PREFIX = "optimistic:";

/** An id for a row the server has not created yet. Never link to it or fetch it. */
export function newOptimisticId(): string {
  return `${OPTIMISTIC_PREFIX}${crypto.randomUUID()}`;
}

export function isOptimisticId(id: string): boolean {
  return id.startsWith(OPTIMISTIC_PREFIX);
}

export function optimisticMutation<TData, TInput>(
  queryClient: QueryClient,
  key: QueryKey,
  apply: (old: TData | undefined, input: TInput) => TData | undefined,
) {
  return {
    onMutate: async (input: TInput) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TData>(key);
      queryClient.setQueryData<TData>(key, (old) => apply(old, input));
      return { previous };
    },
    onError: (
      _error: Error,
      _input: TInput,
      context: { previous: TData | undefined } | undefined,
    ) => {
      if (context) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  };
}
